import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const styles = {
  container: { padding: '20px' },
  filtersContainer: {
    background: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e9ecef',
  },
  filterGroup: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' },
  filterItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filterLabel: { fontSize: '0.8rem', fontWeight: 600, color: '#495057' },
  input: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.95rem' },
  select: { padding: '8px 10px', borderRadius: '6px', border: '1px solid #dee2e6', minWidth: '160px' },
  /** 연도·월만 한 줄에서 공간 절약 (사업단/사업명 select는 styles.select 유지) */
  inputYear: {
    padding: '8px 6px',
    borderRadius: '6px',
    border: '1px solid #dee2e6',
    fontSize: '0.95rem',
    width: '88px',
    minWidth: '88px',
    maxWidth: '88px',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  selectMonth: {
    padding: '8px 6px',
    borderRadius: '6px',
    border: '1px solid #dee2e6',
    fontSize: '0.95rem',
    width: '86px',
    minWidth: '86px',
    maxWidth: '86px',
    boxSizing: 'border-box',
  },
  button: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  primaryButton: { background: '#0d6efd', color: '#fff' },
  secondaryButton: { background: '#20c997', color: '#fff' },
  outlineTempButton: {
    padding: '8px 14px',
    borderRadius: '6px',
    border: '1px solid #fd7e14',
    background: '#fff',
    color: '#c2410c',
    cursor: 'pointer',
    fontWeight: 500,
  },
  tableContainer: {
    marginTop: '16px',
    overflowX: 'auto',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' },
  th: {
    borderBottom: '2px solid #dee2e6',
    padding: '10px',
    background: '#f8f9fa',
    textAlign: 'center',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  td: { borderBottom: '1px solid #eee', padding: '8px', textAlign: 'center', whiteSpace: 'nowrap' },
  countDisplay: { fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '12px 0' },
  noticeBox: {
    background: '#f0f7ff',
    border: '1px solid #cce5ff',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px',
    fontSize: '0.9rem',
    color: '#004085',
    lineHeight: 1.6,
  },
};

const LeaveVerifyPage = () => {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [projectId, setProjectId] = useState('');
  const [projects, setProjects] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1); // 1~12

  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');

  const [results, setResults] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL | NORMAL | MISMATCH | NOT_FOUND
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // 근무 마감 섹션
  const [projectsStatusMap, setProjectsStatusMap] = useState({});
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeMessage, setCloseMessage] = useState('');
  const [closeError, setCloseError] = useState('');
  const [isProjectClosed, setIsProjectClosed] = useState(false);

  const [showApplyPayrollModal, setShowApplyPayrollModal] = useState(false);
  const [applyPayrollLoading, setApplyPayrollLoading] = useState(false);
  const [applyImportErrors, setApplyImportErrors] = useState([]);
  const [showMismatchNoticeModal, setShowMismatchNoticeModal] = useState(false);
  const [mismatchNoticeCount, setMismatchNoticeCount] = useState(0);
  /** null | 'normal' | 'temporary' — 엑셀 출력 시 미마감 허용 여부 등 */
  const [verifySessionKind, setVerifySessionKind] = useState(null);
  const [showTemporaryResultModal, setShowTemporaryResultModal] = useState(false);

  const fileInputRef = useRef(null);
  const clearVerifyFileSelection = useCallback(() => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!year) return;
    try {
      const res = await api.get('/projects', { params: { year } });
      setProjects(res.data || []);
    } catch {
      setProjects([]);
    }
  }, [year]);

  const fetchProjectsStatus = useCallback(async () => {
    if (!year || !month) return;
    try {
      const res = await api.get('/work/projects-status', { params: { year, month } });
      setProjectsStatusMap(res.data || {});
    } catch {
      setProjectsStatusMap({});
    }
  }, [year, month]);

  const fetchProjectStatus = useCallback(async () => {
    if (!year || !month || !projectId) return;
    setCloseLoading(true);
    setCloseError('');
    try {
      const res = await api.get('/work/project-status', { params: { year, month, projectId } });
      setIsProjectClosed(res.data.closed);
    } catch {
      setIsProjectClosed(false);
    } finally {
      setCloseLoading(false);
    }
  }, [year, month, projectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchProjectsStatus();
  }, [fetchProjectsStatus]);

  useEffect(() => {
    fetchProjectStatus();
  }, [fetchProjectStatus]);

  const handleWorkStatusToggle = async (newStatus) => {
    if (!year || !month || !projectId) {
      setCloseError('연도, 월, 사업명을 선택해주세요.');
      return;
    }
    const actionText = newStatus === 'closed' ? '마감' : '마감 해제';
    if (!window.confirm(`[${year}년 ${month}월] ${actionText} 처리를 하시겠습니까?`)) return;
    setCloseLoading(true);
    setCloseError('');
    setCloseMessage('');
    try {
      const res = await api.post('/work/status', {
        year,
        month,
        projectId: Number(projectId),
        newStatus,
      });
      setCloseMessage(res.data.message);
      fetchProjectStatus();
      fetchProjectsStatus();
    } catch (err) {
      setCloseError(err.response?.data?.message || `${actionText} 처리 실패`);
    } finally {
      setCloseLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResults([]);
    setStatusFilter('ALL');
    setMessage('');
    setError('');
    setShowApplyPayrollModal(false);
    setShowMismatchNoticeModal(false);
    setMismatchNoticeCount(0);
    setApplyImportErrors([]);
    setVerifySessionKind(null);
    setShowTemporaryResultModal(false);
  };

  const executeVerify = async (temporary) => {
    if (!year || !projectId || !month || !file) {
      setError('연도, 사업단, 월, 엑셀 파일을 모두 선택해야 합니다.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    setApplyImportErrors([]);
    setResults([]);
    setShowMismatchNoticeModal(false);
    setMismatchNoticeCount(0);
    setShowTemporaryResultModal(false);

    const formData = new FormData();
    formData.append('year', year);
    formData.append('projectId', projectId);
    formData.append('month', month);
    formData.append('excelFile', file);
    if (statusFilter && statusFilter !== 'ALL') {
      formData.append('statusFilter', statusFilter);
    }
    if (password) {
      formData.append('password', password);
    }
    if (temporary) {
      formData.append('temporary', 'true');
    }

    let deferFileClearForPayrollModal = false;
    let keepFileAfterTemporarySuccess = false;
    try {
      const res = await api.post('/leave/verify', formData);
      const rows = res.data || [];
      setResults(rows);
      const allOk = rows.length > 0 && rows.every((r) => r.status === '정상');
      const nonNormalCount = rows.filter((r) => r.status !== '정상').length;

      if (temporary) {
        setVerifySessionKind('temporary');
        setMessage(`임시검증결과: 검증 완료 (총 ${rows.length}행)`);
        setShowApplyPayrollModal(false);
        setShowMismatchNoticeModal(false);
        setMismatchNoticeCount(0);
        setShowTemporaryResultModal(true);
        keepFileAfterTemporarySuccess = true;
      } else {
        setVerifySessionKind('normal');
        setMessage(`검증 완료 (총 ${rows.length}행)`);
        if (allOk) {
          deferFileClearForPayrollModal = true;
          setShowApplyPayrollModal(true);
          setShowMismatchNoticeModal(false);
          setMismatchNoticeCount(0);
        } else {
          setShowApplyPayrollModal(false);
          if (rows.length > 0 && nonNormalCount > 0) {
            setMismatchNoticeCount(nonNormalCount);
            setShowMismatchNoticeModal(true);
          } else {
            setShowMismatchNoticeModal(false);
            setMismatchNoticeCount(0);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || '검증에 실패했습니다.');
    } finally {
      setLoading(false);
      if (!deferFileClearForPayrollModal && !keepFileAfterTemporarySuccess) {
        clearVerifyFileSelection();
      }
    }
  };

  const handleVerify = (e) => {
    e.preventDefault();
    executeVerify(false);
  };

  const handleTemporaryVerify = (e) => {
    e.preventDefault();
    executeVerify(true);
  };

  const handleApplyPayrollConfirm = async () => {
    if (!year || !projectId || !month || !file) {
      setError('연도, 사업단, 월, 엑셀 파일이 필요합니다.');
      setShowApplyPayrollModal(false);
      clearVerifyFileSelection();
      return;
    }
    setApplyPayrollLoading(true);
    setError('');
    setApplyImportErrors([]);
    try {
      const formData = new FormData();
      formData.append('year', year);
      formData.append('projectId', projectId);
      formData.append('month', month);
      formData.append('excelFile', file);
      if (password) {
        formData.append('password', password);
      }
      const res = await api.post('/leave/verify/apply-payroll', formData);
      const { importedCount, errorCount, errors: applyErrors } = res.data || {};
      setShowApplyPayrollModal(false);
      setApplyImportErrors(Array.isArray(applyErrors) ? applyErrors : []);
      setMessage(
        `급여 반영 완료: ${importedCount ?? 0}건 반영, 제외 ${errorCount ?? 0}건. 참여자 급여 관리에서 확인할 수 있습니다.`,
      );
    } catch (err) {
      setError(err.response?.data?.message || '급여 반영에 실패했습니다.');
      setShowApplyPayrollModal(false);
    } finally {
      setApplyPayrollLoading(false);
      clearVerifyFileSelection();
    }
  };

  const handleExport = async () => {
    if (!year || !projectId || !month || !file) {
      setError('연도, 사업단, 월, 엑셀 파일을 모두 선택한 뒤 엑셀 출력을 시도하세요.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');

    const formData = new FormData();
    formData.append('year', year);
    formData.append('projectId', projectId);
    formData.append('month', month);
    formData.append('excelFile', file);
    if (password) {
      formData.append('password', password);
    }
    if (verifySessionKind === 'temporary') {
      formData.append('temporary', 'true');
    }

    try {
      const res = await api.post('/leave/verify/export', formData, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `leave_verify_${year}_${projectId}_${month}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || '검증 결과 엑셀 출력에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const projectOptions = projects || [];
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const statusOptions = [
    { value: 'ALL', label: '전체' },
    { value: 'NORMAL', label: '정상' },
    { value: 'MISMATCH', label: '불일치' },
    { value: 'NOT_FOUND', label: '참여자 없음' },
  ];

  const summary = results.reduce(
    (acc, r) => {
      acc.total += 1;
      if (r.status === '정상') acc.normal += 1;
      else if (r.status === '참여자 없음') acc.notFound += 1;
      else acc.mismatch += 1;
      return acc;
    },
    { total: 0, normal: 0, mismatch: 0, notFound: 0 },
  );

  const filteredResults = results.filter((r) => {
    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'NORMAL') return r.status === '정상';
    if (statusFilter === 'NOT_FOUND') return r.status === '참여자 없음';
    if (statusFilter === 'MISMATCH') return r.status !== '정상' && r.status !== '참여자 없음';
    return true;
  });

  const temporaryNonNormalCount = results.filter((r) => r.status !== '정상').length;

  return (
    <div style={styles.container}>
      <h2>급여 검증 및 입력</h2>

      <div style={styles.noticeBox}>
        <strong>안내</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
          <li>연도·사업단·월을 선택한 뒤, 해당 사업단 검증용 엑셀 파일을 업로드하세요. 성명·주민번호가 <strong>둘 다 있는 행만</strong> 결과에 표시되며, 해당 사업단 명단에서 매칭합니다.</li>
          <li>엑셀에는 최소한 <strong>[성명], [주민등록번호], [실제근무시간], [연차사용시간], [연차수당]</strong> 컬럼이 포함되어야 합니다. <strong>[근무월]</strong> 컬럼이 있으면 선택한 월과 같은 행만 검증합니다.</li>
          <li>사회보험 검증(제목명 고정): 엑셀 <strong>[건강보험]</strong>·<strong>[장기요양보험]</strong>·<strong>[고용보험]</strong>은 선택한 사업단·해당 연·월의 <strong>사회보험 입력(본인·개인 부담)</strong>과 비교합니다. 제목행이 2줄인 경우에도 위 이름이 최종 컬럼명으로 잡히면 됩니다.</li>
          <li>위 세 컬럼 중 <strong>엑셀에 없는 이름</strong>은 <strong>모든 데이터 행</strong>에서 해당 항목만 <strong>불일치</strong>로 처리합니다.</li>
          <li><strong>근무검증:</strong> (엑셀 실제근무시간 + 엑셀 연차사용시간) = 참여자 근무관리 해당 월 총근무시간</li>
          <li><strong>연차검증:</strong> 엑셀 연차사용시간 = 참여자 연차현황 해당 월 사용 연차(시간), 연차수당 = 해당 월 정산지급액</li>
          <li>비밀번호가 설정된 엑셀은 비밀번호를 입력해야 복호화 후 검증이 가능합니다.</li>
          <li>
            검증 결과가 <strong>전부 정상</strong>이면, 같은 엑셀로 <strong>해당 연·월·사업단 급여를 반영</strong>할지
            묻는 창이 뜹니다. (참여자 급여 관리의 엑셀 불러오기와 동일한 방식입니다.) 참여자 급여 관리 화면에서는{' '}
            <strong>관리자만 수정·엑셀 일괄</strong>할 수 있고, 일반 사용자는 <strong>조회</strong>만 가능합니다.
          </li>
          <li>
            <strong>임시검증</strong>은 미마감 사업단에서도 검증만 수행합니다. 급여 반영(등록)은 불가하며, 임시검증은{' '}
            <strong>검증 이력에 남지 않습니다</strong>. 급여까지 반영하려면 근무 월을 마감한 뒤 <strong>검증 실행</strong>을
            다시 하세요.
          </li>
        </ul>
      </div>

      {/* 근무 마감 섹션 */}
      <div style={{ ...styles.filtersContainer, marginBottom: '20px' }}>
        <div style={{ marginBottom: '12px' }}>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>근무 마감</span>
          <span style={{ fontSize: '0.85rem', color: '#6c757d', marginLeft: '10px', fontWeight: 400 }}>
            (본 기능은 참여자 근무 관리의 월 마감과 동일합니다)
          </span>
        </div>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>연도</span>
            <input
              type="number"
              style={styles.inputYear}
              value={year}
              min="2020"
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>월</span>
            <select style={styles.selectMonth} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{m}월</option>
              ))}
            </select>
          </div>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>사업명</span>
            <select style={styles.select} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">-- 사업명 선택 --</option>
              {projectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.projectName}({projectsStatusMap[p.id] ? '마감' : '미마감'})
                </option>
              ))}
            </select>
          </div>
          <div style={{ ...styles.filterItem, alignItems: 'center' }}>
            <span style={styles.filterLabel}>상태</span>
            <span style={{ padding: '8px 0', fontSize: '0.95rem', fontWeight: 500 }}>
              {closeLoading ? '조회 중...' : projectId ? (isProjectClosed ? '마감' : '미마감') : '-'}
            </span>
          </div>
          {user?.role === 'admin' && (
            <>
              <div style={styles.filterItem}>
                <span style={styles.filterLabel}>&nbsp;</span>
                <button
                  type="button"
                  style={{ ...styles.button, backgroundColor: '#dc3545', color: '#fff' }}
                  disabled={closeLoading || !projectId || isProjectClosed}
                  onClick={() => handleWorkStatusToggle('closed')}
                >
                  마감하기
                </button>
              </div>
              <div style={styles.filterItem}>
                <span style={styles.filterLabel}>&nbsp;</span>
                <button
                  type="button"
                  style={{ ...styles.button, backgroundColor: '#ffc107', color: '#000' }}
                  disabled={closeLoading || !projectId || !isProjectClosed}
                  onClick={() => handleWorkStatusToggle('open')}
                >
                  마감 해제하기
                </button>
              </div>
            </>
          )}
        </div>
        {closeMessage && <p style={{ color: 'green', margin: '8px 0 0', fontSize: '0.9rem' }}>{closeMessage}</p>}
        {closeError && <p style={{ color: 'red', margin: '8px 0 0', fontSize: '0.9rem' }}>{closeError}</p>}
      </div>

      <form onSubmit={handleVerify}>
        <div style={styles.filtersContainer}>
          <div style={styles.filterGroup}>
            <div style={styles.filterItem}>
              <span style={styles.filterLabel}>연도</span>
              <input
                type="number"
                style={styles.inputYear}
                value={year}
                min="2020"
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
            <div style={styles.filterItem}>
              <span style={styles.filterLabel}>사업단</span>
              <select
                style={styles.select}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">-- 사업단 선택 --</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.projectName}({projectsStatusMap[p.id] ? '마감' : '미마감'})
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.filterItem}>
              <span style={styles.filterLabel}>월</span>
              <select
                style={styles.selectMonth}
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {monthOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}월
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.filterItem}>
              <span style={styles.filterLabel}>엑셀 파일</span>
              <input
                id="leave-verify-file"
                ref={fileInputRef}
                type="file"
                style={styles.input}
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
            </div>
            <div style={styles.filterItem}>
              <span style={styles.filterLabel}>엑셀 비밀번호 (있는 경우만)</span>
              <input
                type="password"
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호 없는 엑셀은 비워두세요"
              />
            </div>
            <button
              type="submit"
              style={{ ...styles.button, ...styles.primaryButton }}
              disabled={loading}
            >
              {loading ? '검증 중...' : '검증 실행'}
            </button>
            <button
              type="button"
              style={{
                ...styles.outlineTempButton,
                opacity: loading ? 0.55 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              disabled={loading}
              onClick={handleTemporaryVerify}
            >
              임시검증
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.secondaryButton }}
              disabled={loading || !file}
              onClick={handleExport}
            >
              엑셀 출력
            </button>
          </div>
        </div>
      </form>

      {message && <p style={{ color: 'green', marginBottom: '8px' }}>{message}</p>}
      {error && <p style={{ color: 'red', marginBottom: '8px' }}>{error}</p>}
      {applyImportErrors.length > 0 && (
        <div
          style={{
            marginBottom: '12px',
            padding: '12px 16px',
            background: '#fff8e6',
            border: '1px solid #f5d78e',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#7c5e10',
          }}
        >
          <strong>급여 반영 시 제외·오류 항목</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
            {applyImportErrors.slice(0, 30).map((item, idx) => (
              <li key={idx}>
                {item.row?.name || '성명 미상'} / {item.reason}
              </li>
            ))}
            {applyImportErrors.length > 30 && <li>… 외 {applyImportErrors.length - 30}건</li>}
          </ul>
        </div>
      )}

      <div style={styles.countDisplay}>
        {loading
          ? '검증 중...'
          : verifySessionKind === 'temporary'
          ? `임시검증결과: ${summary.total}행 (정상 ${summary.normal}행/불일치 ${summary.mismatch}행/참여자 없음 ${summary.notFound}행)`
          : `검증 결과: ${summary.total}행 (정상 ${summary.normal}행/불일치 ${summary.mismatch}행/참여자 없음 ${summary.notFound}행)`}
      </div>

      <div style={styles.filtersContainer}>
        <div style={styles.filterGroup}>
          <div style={styles.filterItem}>
            <span style={styles.filterLabel}>상태</span>
            <select
              style={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={loading || results.length === 0}
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {!loading && results.length > 0 && statusFilter !== 'ALL' && (
            <div style={{ alignSelf: 'flex-end', fontSize: '0.9rem', color: '#495057' }}>
              표시 중: {filteredResults.length}행
            </div>
          )}
        </div>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>행번호</th>
              <th style={styles.th}>성명</th>
              <th style={styles.th}>주민번호(일부)</th>
              <th style={styles.th}>사업단</th>
              <th style={styles.th}>엑셀 실제근무</th>
              <th style={styles.th}>엑셀 연차사용</th>
              <th style={styles.th}>시스템 총근무</th>
              <th style={styles.th}>근무 일치</th>
              <th style={styles.th}>시스템 해당월연차</th>
              <th style={styles.th}>연차사용 일치</th>
              <th style={styles.th}>엑셀 연차수당</th>
              <th style={styles.th}>시스템 연차수당</th>
              <th style={styles.th}>연차수당 일치</th>
              <th style={styles.th}>엑셀 건강보험</th>
              <th style={styles.th}>사회보험 건강</th>
              <th style={styles.th}>건강 일치</th>
              <th style={styles.th}>엑셀 장기요양</th>
              <th style={styles.th}>사회보험 요양</th>
              <th style={styles.th}>요양 일치</th>
              <th style={styles.th}>엑셀 고용</th>
              <th style={styles.th}>사회보험 고용</th>
              <th style={styles.th}>고용 일치</th>
              <th style={styles.th}>상태</th>
              <th style={styles.th}>어디에 없음</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.length === 0 ? (
              <tr>
                <td colSpan={24} style={styles.td}>
                  검증된 데이터가 없습니다.
                </td>
              </tr>
            ) : (
              filteredResults.map((r, idx) => {
                const rrnDisplay = r.rrn ? `${r.rrn.substring(0, 8)}...` : '-';
                const workMatchText = r.systemWorkHours == null ? '-' : r.workMatch ? '일치' : '불일치';
                const leaveUsageMatchText =
                  r.systemMonthUsedLeaveHours == null ? '-' : r.leaveUsageMatch ? '일치' : '불일치';
                const leavePayMatchText =
                  r.systemLeavePay == null ? '-' : r.leavePayMatch ? '일치' : '불일치';
                const insMatchText = (matched) => (matched ? '일치' : '불일치');
                const fmtMoney = (v) =>
                  v === null || v === undefined || v === '' ? '-' : Number(v).toLocaleString();
                const missingInText =
                  r.missingIn === 'SYSTEM' ? '시스템에 없음' : r.missingIn === 'EXCEL' ? '엑셀에 없음' : '-';
                const rowStyle =
                  r.status === '정상'
                    ? {}
                    : r.status === '참여자 없음'
                    ? { backgroundColor: '#ffe3e3' }
                    : { backgroundColor: '#fff3cd' };

                return (
                  <tr key={idx} style={rowStyle}>
                    <td style={styles.td}>{r.rowNumber ?? '-'}</td>
                    <td style={styles.td}>{r.name}</td>
                    <td style={styles.td}>{rrnDisplay}</td>
                    <td style={styles.td}>{r.projectName || '-'}</td>
                    <td style={styles.td}>{r.excelWorkHours ?? '-'}</td>
                    <td style={styles.td}>{r.excelLeaveUsageHours ?? '-'}</td>
                    <td style={styles.td}>
                      {r.systemWorkHours != null ? r.systemWorkHours : '-'}
                    </td>
                    <td style={styles.td}>{workMatchText}</td>
                    <td style={styles.td}>
                      {r.systemMonthUsedLeaveHours != null ? r.systemMonthUsedLeaveHours : '-'}
                    </td>
                    <td style={styles.td}>{leaveUsageMatchText}</td>
                    <td style={styles.td}>{r.excelLeavePay ?? '-'}</td>
                    <td style={styles.td}>
                      {r.systemLeavePay != null ? r.systemLeavePay.toLocaleString() : '-'}
                    </td>
                    <td style={styles.td}>{leavePayMatchText}</td>
                    <td style={styles.td}>{fmtMoney(r.excelHealthInsurance)}</td>
                    <td style={styles.td}>{fmtMoney(r.systemHealthInsurance)}</td>
                    <td style={styles.td}>{insMatchText(r.healthInsuranceMatch)}</td>
                    <td style={styles.td}>{fmtMoney(r.excelLongTermCareInsurance)}</td>
                    <td style={styles.td}>{fmtMoney(r.systemLongTermCareInsurance)}</td>
                    <td style={styles.td}>{insMatchText(r.longTermCareInsuranceMatch)}</td>
                    <td style={styles.td}>{fmtMoney(r.excelEmploymentInsurance)}</td>
                    <td style={styles.td}>{fmtMoney(r.systemEmploymentInsurance)}</td>
                    <td style={styles.td}>{insMatchText(r.employmentInsuranceMatch)}</td>
                    <td style={styles.td}>{r.status}</td>
                    <td style={styles.td}>{missingInText}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #dee2e6',
          fontSize: '0.9rem',
          color: '#495057',
        }}
      >
        과거 <strong>검증 실행</strong> 기록은{' '}
        <Link to="/leave-verify/history" style={{ fontWeight: 600 }}>
          검증 이력 조회
        </Link>
        에서 연도·월·사업단·검증 상태로 찾을 수 있습니다. <strong>임시검증</strong>은 이력에 저장되지 않습니다.
      </div>

      {showTemporaryResultModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="temporary-verify-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div
              id="temporary-verify-modal-title"
              style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '12px', color: '#c2410c' }}
            >
              임시검증 안내
            </div>
            <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.65, margin: 0 }}>
              임시검증이 완료되었습니다. (총 <strong>{results.length}</strong>행)
              {temporaryNonNormalCount > 0 && (
                <>
                  {' '}
                  정상이 아닌 행이 <strong>{temporaryNonNormalCount}</strong>건 있습니다. 아래 표에서 내용을 확인한 뒤
                  엑셀·시스템 값을 맞춰 주세요.
                </>
              )}
            </p>
            <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.65, margin: '14px 0 0' }}>
              <strong>급여를 시스템에 등록(반영)하려면</strong> 해당 연·월·사업단의 <strong>근무관리를 먼저 마감</strong>한 뒤,
              같은 엑셀로 화면의 <strong>검증 실행</strong>을 다시 진행해 주세요. 검증 결과가 <strong>모두 정상</strong>일
              때만 급여 반영을 진행할 수 있습니다.
            </p>
            <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.55, margin: '12px 0 0' }}>
              임시검증 결과는 검증 이력에 남지 않습니다.
            </p>
            <div style={{ marginTop: '22px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton, minWidth: '88px' }}
                onClick={() => setShowTemporaryResultModal(false)}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {showMismatchNoticeModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mismatch-notice-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div id="mismatch-notice-modal-title" style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '12px' }}>
              검증 결과 안내
            </div>
            <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.6, margin: 0 }}>
              검증 결과 <strong>정상이 아닌 행</strong>이 <strong>{mismatchNoticeCount}건</strong> 있습니다. 이 상태에서는
              급여 반영(급여 등록)으로 이어지지 않습니다.
            </p>
            <p
              style={{
                fontSize: '0.95rem',
                color: '#334155',
                lineHeight: 1.6,
                margin: '12px 0 0',
              }}
            >
              아래 표에서 해당 행을 확인한 뒤, 급여·사회보험 입력 등 시스템 값과 엑셀을 맞추고{' '}
              <strong>다시 검증</strong>해 주세요. <strong>모든 행이 정상</strong>일 때만 급여 반영을 진행할 수 있습니다.
            </p>
            <div style={{ marginTop: '22px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton, minWidth: '88px' }}
                onClick={() => setShowMismatchNoticeModal(false)}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {showApplyPayrollModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="apply-payroll-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div id="apply-payroll-modal-title" style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '12px' }}>
              급여 반영
            </div>
            <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.55, margin: 0 }}>
              <strong>
                {year}년 {month}월
              </strong>
              · 선택한 사업단 기준으로, 방금 검증한 <strong>동일 엑셀</strong>로 참여자 급여를 등록(갱신)하시겠습니까?
              <br />
              <span style={{ fontSize: '0.88rem', color: '#64748b' }}>
                참여자 급여 관리의 &quot;엑셀 불러오기&quot;와 같은 처리입니다.
              </span>
            </p>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                style={{
                  ...styles.button,
                  background: '#6c757d',
                  color: '#fff',
                  opacity: applyPayrollLoading ? 0.6 : 1,
                }}
                disabled={applyPayrollLoading}
                onClick={() => {
                  setShowApplyPayrollModal(false);
                  clearVerifyFileSelection();
                }}
              >
                취소
              </button>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity: applyPayrollLoading ? 0.7 : 1,
                }}
                disabled={applyPayrollLoading}
                onClick={handleApplyPayrollConfirm}
              >
                {applyPayrollLoading ? '반영 중…' : '확인'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveVerifyPage;

